import { type FC } from "react";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { type OrderbookRowItem } from "~/features/orderbook/models/orderbookModels";
import classes from "./OrderbookResultsTable.module.less";
import { Link, LinkType } from "~/common/components/Link";
import { getOrderDetailsUrl } from "~/utils/url/urlUtils";
import { type TFunction } from "i18next";
import { fakeDescriptorFromProduct } from "~/domains/product/utils/productDescriptorUtils";
import { getPackagingLabelAtLineItem } from "~/features/cart/cart/utils/cartUtils";
import { translateWarehouseName } from "~/utils/language/translationUtils";
import { IconButton, Stack, Tooltip, Typography } from "~/common/mui";
import {
  type PriceProps,
  PriceType,
  obtainPriceString,
  obtainUnitLabel,
} from "~/domains/pricing/components/Price/v1/Price";
import { nonVolatileText } from "~/utils/PriceUtils";
import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import { formatShippingOrDeliveryDate } from "~/common/utils/dateUtils";
import Tag from "~/common/components/Tag/Tag";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import dayjs from "dayjs";
import classNames from "classnames";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Product } from "~/domains/product/Product";
import { C_ON_SURFACE_LOW_EMPHASIS } from "~/common/components/icon/Colors";
import { ProductAliases } from "~/domains/product/components/ProductAliases";
import { TooltipWithArrow } from "~/common/components/TooltipWithArrow";

export const AccountCell: FC<{ item: OrderbookRowItem }> = ({ item }) => (
  <>
    <Typography>
      {item.account?.shortCode}, {item.account?.name}
    </Typography>
    <Typography color={C_ON_SURFACE_LOW_EMPHASIS}>
      {item.account?.endCustomerCode}, {item.account?.endCustomerName}
    </Typography>
  </>
);

export const SuccessorRow: FC<{ product: OrderbookRowItem["product"] }> = ({ product }) => (
  <Stack className={classes.extraRow}>
    <NewVersionMessage baseProduct={product} />
  </Stack>
);

export const SalesOrderCell: FC<{ item: OrderbookRowItem }> = ({ item }) => (
  <>
    <Link href={getOrderDetailsUrl(item.schaefflerOrderNo)} target="_blank" variant={LinkType.SECONDARY}>
      {item.schaefflerOrderNo}
    </Link>
    <Typography variant="bodySmall" color={C_ON_SURFACE_LOW_EMPHASIS}>
      {item.orderLineNo}
    </Typography>
  </>
);

export const SchaefflerIdCell: FC<{ item: OrderbookRowItem; t: TFunction }> = ({ item }) => {
  return (
    <>
      <Typography>{item.product.sapMaterialNumber}</Typography>
      <Typography variant="bodySmall" color={C_ON_SURFACE_LOW_EMPHASIS}>
        <ProductAliases product={item.product} />
      </Typography>
    </>
  );
};

export const PurchaseOrderCell: FC<{ item: OrderbookRowItem }> = ({ item }) => (
  <Typography>{item.customerOrderNo}</Typography>
);

export const PackagingCell: FC<{ item: OrderbookRowItem; t: TFunction }> = ({ item, t }) => {
  const fakeDescriptor = fakeDescriptorFromProduct(item.product);
  const quantityVisible = item.product.quantityVisible;

  return (
    <>
      <Typography>
        {getPackagingLabelAtLineItem({
          t,
          productDescriptor: fakeDescriptor,
          quantity: item.totalQuantity,
          packCode: item.packCode,
          packSize: item.packSize,
        })}
      </Typography>
      {quantityVisible && (
        <Typography variant="bodySmall" color={C_ON_SURFACE_LOW_EMPHASIS}>
          {translateWarehouseName(t, item.primaryWarehouseName)}
        </Typography>
      )}
    </>
  );
};

export const ProductCell: FC<{ item: OrderbookRowItem; t: TFunction }> = ({ item }) => {
  return (
    <>
      <Product.PdpLink product={item.product} target="_blank">
        <Product.DesignationV2 variant="bodyMedium" fontWeight={"bold"} product={item.product} />
      </Product.PdpLink>
      {item.product.marketDesignation && <Product.MMDTag product={item.product} />}
      {item.product.shortDescription && (
        <Product.ShortDescriptionV2 variant="bodySmall" color={C_ON_SURFACE_LOW_EMPHASIS} product={item.product} />
      )}
    </>
  );
};

export const QuantityCell: FC<{ item: OrderbookRowItem; t: TFunction }> = ({ item, t }) => (
  <>
    <Typography>
      {t("page.abmonitor.confirmedQuantity", {
        current: item.curQuantity,
        total: item.totalQuantity,
      })}
    </Typography>
    <Typography variant="bodySmall" color={C_ON_SURFACE_LOW_EMPHASIS}>
      {item.prevQuantity}
    </Typography>
  </>
);

export const NetPriceCell: FC<{ item: OrderbookRowItem; t: TFunction }> = ({ item, t }) => {
  const priceProps: PriceProps = {
    price: item.netPrice,
    unit: item.netPrice.unit,
    isSubscription: item.netPrice.isSubscription,
    variant: PriceType.ProductNetPrice,
  };
  return (
    <>
      <Typography>{obtainPriceString(priceProps, t, undefined)}</Typography>
      <Typography variant="bodySmall" color={C_ON_SURFACE_LOW_EMPHASIS}>
        {obtainUnitLabel(priceProps, t)}
      </Typography>
    </>
  );
};

export const NetValueCell: FC<{ item: OrderbookRowItem }> = ({ item }) => (
  <Typography>{nonVolatileText(item.totalPrice)}</Typography>
);

export const ActionButtonCell: FC<{ item: OrderbookRowItem; t: TFunction }> = ({ item, t }) => (
  <Stack className="action-cell" sx={{ justifyContent: " center", flexDirection: "row" }}>
    <Link href={getOrderDetailsUrl(item.schaefflerOrderNo)} target="_blank">
      <TooltipWithArrow title={t("page.abmonitor.tableRow.viewOrder")} placement="top">
        <IconButton color="primary">
          <OpenInNewIcon color="primary" />
        </IconButton>
      </TooltipWithArrow>
    </Link>

    {/* Hiding this for the now, will be use in future stories */}
    <Tooltip
      title={t("page.abmonitor.tableRow.serviceCases")}
      arrow
      placement="top"
      sx={{ cursor: "pointer", display: "none" }}
    >
      <IconButton color="primary">
        <DescriptionOutlinedIcon color="primary" />
      </IconButton>
    </Tooltip>
  </Stack>
);

export const DeliveryCell: FC<{
  item: OrderbookRowItem;
  t: TFunction;
  isDistributorOrPurchaser: boolean;
}> = ({ item, isDistributorOrPurchaser }) => {
  const currentDeliveryDate = dayjs(item.curDeliveryDate);
  const previousDeliveryDate = dayjs(item.prevDeliveryDate);
  const isSameDeliveryDate = currentDeliveryDate.isSame(previousDeliveryDate);
  const isPositiveDeliveryDate = currentDeliveryDate.isBefore(previousDeliveryDate);

  const renderCurrentDeliveryDate = () => {
    if (item.curDeliveryDate && item.prevDeliveryDate && isDistributorOrPurchaser && !isSameDeliveryDate) {
      return (
        <Tag
          className={classes.curDeliveryDate}
          variant={isPositiveDeliveryDate ? "success" : "warn"}
          icon={
            isPositiveDeliveryDate ? (
              <CheckCircleOutlinedIcon className={classes.icon} fontSize="small" />
            ) : (
              <ErrorOutlineIcon className={classNames(classes.icon, classes.yellow)} fontSize="small" />
            )
          }
        >
          {formatShippingOrDeliveryDate(item.curDeliveryDate, DateFormatType.DATE)}
        </Tag>
      );
    }
    return (
      <Typography className={classes.confirmedDeliveryDate}>
        {formatShippingOrDeliveryDate(item.curDeliveryDate, DateFormatType.DATE)}
      </Typography>
    );
  };

  return (
    <>
      {renderCurrentDeliveryDate()}
      <Typography color={C_ON_SURFACE_LOW_EMPHASIS} className={classNames(classes.confirmedDeliveryDate)}>
        {formatNullableDate(item.prevDeliveryDate, DateFormatType.DATE, "-")}
      </Typography>
    </>
  );
};

export const RequestedDeliveryCell: FC<{ item: OrderbookRowItem }> = ({ item }) => (
  <Typography>{formatNullableDate(item.requestedDeliveryDate, DateFormatType.DATE, "-")}</Typography>
);

export const columnSizes = {
  salesOrder: 160,
  purchaseOrder: 136,
  product: 240,
  schaefflerId: 144,
  packaging: 200,
  quantity: 120,
  netPrice: 136,
  netValue: 136,
  deliveryDate: 152,
  requestedDate: 120,
  actions: 104,
} as const;
